import {
	Button,
	CategorySelect,
	Checkbox,
	ChoiceChip,
	Input,
	loadCategories,
	Modal,
	ModalBody,
	ModalContent,
	NativeSelect,
	Textarea,
} from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { AuthPanel } from "~/common/auth/panel";
import { waitForSubscription } from "~/common/billing/paygate";
import { PlanChooser } from "~/common/billing/plans";
import { SubscriptionPanel } from "~/common/billing/subscription";
import { openSubscriptionCheckout } from "~/common/billing/subscription-checkout";
import { type AnalyzeResult, ApiError, api, type OnboardingState } from "~/core/api/index";
import * as fmt from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";
import { apiError, keys, paid, read } from "~/core/query";
import { useCategoryLabels } from "~/core/ui/category-labels";
import { ModalHeader } from "~/core/ui/dialog";
import { useWorkspaces } from "~/core/workspace";
import { EnginePicker } from "~/modules/analytics/pages/settings";
import { Logo } from "~/shell/logo";

/**
 * From "what is your brand" to a workspace, in one sitting — and not one engine
 * call about the brand itself along the way.
 *
 * The screens follow Profound's order because that order is right: ask for the
 * cheapest thing first (a URL), read the homepage while they wait, show them
 * which audience and engines to track, then ask for money. What the wizard spends is a
 * few cents of Claude for brand extraction and topic suggestions. The
 * measurement — four engines, five replications, real money — is a workspace
 * action after a plan exists to bill it to. An earlier version measured inside
 * the wizard; it cost a dollar before the person had seen a price.
 *
 * Two forks are ondit's own: a brand with no website is described instead of
 * fetched, and a URL that is already a listing is claimed instead of
 * duplicated.
 *
 * State lives on the server, not in this component. Every step writes what it
 * learned, so a reload — or a return next week — resumes where they left off
 * rather than at the beginning.
 *
 * Two ways in. A new account runs all seven screens once. An account adding its
 * second brand runs the four that are about a brand, and every screen names the
 * brand it is writing to, so the account's first brand is never the one that
 * gets overwritten.
 */

type Step = "company" | "website" | "brand" | "region" | "topics" | "engines" | "plan";

/** A new account: from nothing to a workspace. */
const FIRST_RUN: Step[] = ["company", "website", "brand", "region", "topics", "engines", "plan"];

/**
 * The same wizard, for the account's second brand.
 *
 * Four screens rather than seven. Company size, audience region and the plan
 * belong to the account and it answered those the first time; what is left is
 * everything that is about this brand and nothing that is not.
 */
const ADD_BRAND: Step[] = ["website", "brand", "topics", "engines"];

// Resolve category labels before the confirmation form can first render.
async function loadOnboarding(entity?: string): Promise<OnboardingState> {
	const [state] = await Promise.all([api.onboarding.state(entity), loadCategories()]);
	return state;
}

export function OnboardingPage() {
	const state = useQuery({ queryKey: ["onboarding", null], queryFn: () => loadOnboarding(), ...read });

	if (!state.data) return <Opening step="company" error={apiError(state.error)} />;
	return (
		<Flow
			order={FIRST_RUN}
			at={resume(state.data)}
			initial={state.data}
			reload={state.refetch}
			entity={state.data.entity?.id ?? null}
		/>
	);
}

/**
 * Adding a brand to an account that already has one.
 *
 * Starts holding no brand, which is what tells the server to create one rather
 * than write over the brand the account already has.
 */
export function NewBrandPage({ orgId }: { orgId: string }) {
	const navigate = useNavigate();
	const [search] = useSearchParams();
	const [pending] = useState(() => ({ entity: search.get("entity"), step: search.get("step") as Step | null }));
	const state = useQuery({
		queryKey: ["onboarding", pending.entity],
		queryFn: () => loadOnboarding(pending.entity ?? undefined),
		...read,
	});

	if (!state.data) return <Opening step="website" error={apiError(state.error)} />;
	return (
		<Flow
			order={ADD_BRAND}
			at={pending.entity ? (pending.step && ADD_BRAND.includes(pending.step) ? pending.step : "brand") : "website"}
			initial={state.data}
			reload={state.refetch}
			entity={pending.entity}
			orgId={orgId}
			persist
			// The same handoff the first brand gets: the wizard's last screen
			// is what switches the loop on and takes its first pass, and a
			// second brand that skipped it would be measured by nothing.
			//
			// A whole-page load, not a route change: the shell fetched the
			// brand list before this brand existed, and the sidebar would
			// otherwise send the person back to the brand they started from.
			//
			// The overview, like the first brand gets. It used to be the task
			// list, on the reasoning that the first pass is what there is to
			// watch — but a one-row table of a job nobody asked for is not an
			// answer to "what is this product doing for me". The overview says
			// where the brand is around the loop and what the running pass will
			// put there, which is the same question asked properly.
			onDone={async (entityId) => {
				await api.onboarding.finish(entityId, orgId);
				window.location.assign(`/w/${entityId}`);
			}}
			onCancel={() => navigate("/")}
		/>
	);
}

function Opening({ step, error }: { step: Step; error: ApiError | null }) {
	const t = useT();
	const { locale } = useI18n();
	return (
		<div className="login">
			<Panel step={step} />
			<div className="login-form">
				<p className="muted">{error ? error.localized(locale) : t("state.loading")}</p>
			</div>
		</div>
	);
}

function resume(s: OnboardingState): Step {
	if (!s.entity) return s.company_size || s.is_agency ? "website" : "company";
	if (!s.region) return "region";
	if (s.entity.selected_topics.length === 0 && s.question_count === 0) return "topics";
	if (s.entity.engines.length === 0) return "engines";
	return "plan";
}

function Flow({
	order,
	at,
	initial,
	reload,
	entity,
	orgId,
	onDone,
	onCancel,
	persist = false,
}: {
	order: Step[];
	at: Step;
	initial: OnboardingState;
	reload: () => void;
	/** The brand in hand, or null to create one on the first write. */
	entity: string | null;
	/** The account the brand is added to. Absent on the first run, which has one. */
	orgId?: string;
	onDone?: (entityId: string) => void | Promise<void>;
	onCancel?: () => void;
	persist?: boolean;
}) {
	const [step, setStep] = useState<Step>(at);
	const [state, setState] = useState(initial);
	const [entityId, setEntityId] = useState<string | null>(entity);
	const navigate = useNavigate();
	useEffect(() => {
		if (persist && entityId) {
			navigate(`?${new URLSearchParams({ entity: entityId, step })}`, { replace: true });
		}
	}, [persist, entityId, step, navigate]);

	// The id is an argument as well as state because the screen that creates a
	// brand refreshes in the same handler that learns its id, and `useState` has
	// not settled by then.
	const refresh = useCallback(
		async (id?: string) => {
			const next = await api.onboarding.state(id ?? entityId ?? undefined);
			setState(next);
			reload();
			return next;
		},
		[reload, entityId],
	);

	const advance = useCallback(async () => {
		const nextStep = order[order.indexOf(step) + 1];
		if (nextStep) setStep(nextStep);
		else if (entityId) await onDone?.(entityId);
	}, [order, step, entityId, onDone]);

	function back() {
		const i = order.indexOf(step);
		if (i > 0) setStep(order[i - 1]);
	}

	const shared = {
		state,
		entityId,
		orgId,
		setEntityId,
		refresh,
		advance,
		back,
		last: order.indexOf(step) === order.length - 1,
	};
	return (
		<div className="login">
			<Panel step={step} brand={entityId === state.entity?.id ? state.entity?.name : undefined} />
			<div className="login-form">
				<a className="brand login-brand" href="/">
					<Logo size="lg" />
				</a>
				<div className={step === "plan" ? "login-card wizard wizard-wide" : "login-card wizard"}>
					{step === "company" && <CompanyStep {...shared} />}
					{step === "website" && <WebsiteStep {...shared} />}
					{step === "brand" && <BrandStep {...shared} />}
					{step === "region" && <RegionStep {...shared} />}
					{step === "topics" && <TopicsStep {...shared} />}
					{step === "engines" && <EnginesStep {...shared} />}
					{step === "plan" && <PlanStep {...shared} />}
					<Dots order={order} step={step} onBack={step !== order[0] ? back : undefined} onCancel={onCancel} />
				</div>
			</div>
		</div>
	);
}

interface StepProps {
	state: OnboardingState;
	/** The brand this pass is building. Null until a screen writes one. */
	entityId: string | null;
	/** The account it is being added to, when this is not the first run. */
	orgId?: string;
	setEntityId: (id: string) => void;
	refresh: (entityId?: string) => Promise<OnboardingState>;
	/** On to the next screen of this order, or out of the wizard. */
	advance: () => void | Promise<void>;
	back: () => void;
	/** This screen is the last one this order has. */
	last: boolean;
}

/**
 * The stage of the loop each screen of the wizard is buying.
 *
 * Not the stage the screen belongs to — the wizard belongs to none of them —
 * but the one its answer pays for, so the panel beside a field says what that
 * field is worth. Reflow is absent on purpose: it is what a plan buys after
 * the first pass, and there is nothing to reflow while nothing is published.
 */
const STEP_STAGE: Record<Step, string> = {
	company: "detect",
	website: "generate",
	brand: "generate",
	region: "detect",
	topics: "detect",
	engines: "detect",
	plan: "publish",
};

/** The left-hand panel: the same two lines as everywhere, plus a step note. */
function Panel({ step, brand }: { step: Step; brand?: string }) {
	const t = useT();
	return <AuthPanel headline={brand} note={t(`onboard.panel.${step}`)} stage={STEP_STAGE[step]} />;
}

function Dots({
	order,
	step,
	onBack,
	onCancel,
}: {
	order: Step[];
	step: Step;
	onBack?: () => void;
	onCancel?: () => void;
}) {
	const t = useT();
	return (
		<div className="wizard-foot">
			{onBack ? (
				<Button type="button" variant="link" onClick={onBack}>
					‹ {t("onboard.back")}
				</Button>
			) : onCancel ? (
				<Button type="button" variant="link" onClick={onCancel}>
					{t("onboard.cancel")}
				</Button>
			) : (
				<span />
			)}
			{/* A list, so the progress label has an element that supports it. */}
			<ol
				className="wizard-dots"
				aria-label={t("onboard.progress", { n: String(order.indexOf(step) + 1), of: String(order.length) })}
			>
				{order.map((s) => (
					<li key={s} className={s === step ? "on" : ""} />
				))}
			</ol>
		</div>
	);
}

function Problem({ error }: { error: unknown }) {
	const { locale } = useI18n();
	if (!error) return null;
	return (
		<div className="banner banner-warn">{error instanceof ApiError ? error.localized(locale) : String(error)}</div>
	);
}

// -- 1. company --------------------------------------------------------------------

const SIZES = ["1-10", "11-100", "101-500", "501-1000", "1001+"];

function CompanyStep({ state, refresh, advance }: StepProps) {
	const t = useT();
	const [size, setSize] = useState<string | null>(state.company_size);
	const [agency, setAgency] = useState(state.is_agency);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<unknown>(null);

	async function submit(e: FormEvent) {
		e.preventDefault();
		setBusy(true);
		setError(null);
		try {
			await api.onboarding.company({ company_size: size, is_agency: agency });
			await refresh();
			advance();
		} catch (err) {
			setError(err);
		} finally {
			setBusy(false);
		}
	}

	return (
		<form onSubmit={submit} className="stack">
			<h1>{t("onboard.company.title")}</h1>
			<p className="muted">{t("onboard.company.body")}</p>
			<Problem error={error} />
			<fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
				<legend>{t("onboard.company.size")}</legend>
				<div className="wizard-chips">
					{SIZES.map((s) => (
						<ChoiceChip key={s} selected={size === s} onClick={() => setSize(s)}>
							{t("onboard.company.employees", { n: s })}
						</ChoiceChip>
					))}
				</div>
			</fieldset>
			<label htmlFor="agency" className="row" style={{ gap: "var(--s-4)" }}>
				<Checkbox id="agency" checked={agency} onCheckedChange={(checked) => setAgency(checked === true)} />
				{t("onboard.company.agency")}
			</label>
			<Button variant="primary" type="submit" disabled={busy}>
				{t("onboard.continue")}
			</Button>
		</form>
	);
}

// -- 2. website? -------------------------------------------------------------------

function WebsiteStep({ entityId, orgId, setEntityId, refresh, advance }: StepProps) {
	const t = useT();
	const navigate = useNavigate();
	const [website, setWebsite] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<unknown>(null);
	const [existing, setExisting] = useState<AnalyzeResult["existing"] | null>(null);
	// Analysed, and waiting on the person to say they meant it.
	const [held, setHeld] = useState<AnalyzeResult | null>(null);
	const [draft, setDraft] = useState<AnalyzeResult | null>(null);
	// One write per analysis. The effect below re-runs whenever anything it
	// closes over changes, and creating the brand twice would be two brands.
	const written = useRef<AnalyzeResult | null>(null);
	const creationRequest = useRef(crypto.randomUUID());

	async function analyze(e: FormEvent) {
		e.preventDefault();
		setBusy(true);
		setError(null);
		setExisting(null);
		try {
			const result = await api.onboarding.analyze(website, orgId);
			if (result.existing && !result.existing.claimed) {
				navigate(`/claim/${encodeURIComponent(result.existing.slug)}`);
				return;
			}
			// Somebody already holds this host. Said, not refused — one company
			// runs two brands on one site and a domain changes hands — but said
			// before the brand is written, because only they know whether that
			// account is their colleague's.
			if (result.existing) {
				setExisting(result.existing);
				setHeld(result);
				return;
			}
			setDraft(result);
		} catch (err) {
			setError(err);
		} finally {
			setBusy(false);
		}
	}

	// Hand the draft to the brand step through server state: write it, then
	// let the next screen read it back and let the person correct it.
	useEffect(() => {
		if (!draft || written.current === draft) return;
		written.current = draft;
		(async () => {
			setBusy(true);
			try {
				const saved = await api.onboarding.brand({
					request_id: creationRequest.current,
					entity_id: entityId,
					org_id: orgId,
					name: draft.draft?.name ?? draft.page?.title ?? draft.domain ?? "",
					description: draft.draft?.description ?? draft.page?.description ?? "",
					website: draft.domain ?? website,
					categories: draft.draft?.categories ?? [],
					aliases: draft.draft?.aliases ?? [],
				});
				setEntityId(saved.entity_id);
				await refresh(saved.entity_id);
				advance();
			} catch (err) {
				setError(err);
				written.current = null;
				setDraft(null);
			} finally {
				setBusy(false);
			}
		})();
	}, [draft, refresh, advance, setEntityId, entityId, orgId, website]);

	if (busy && draft) {
		return <Working label={t("onboard.website.analyzing", { domain: draft.domain ?? website })} />;
	}

	return (
		<div className="stack">
			<h1>{t("onboard.website.title")}</h1>
			<p className="muted">{t("onboard.website.body")}</p>
			<Problem error={error} />
			{/* Not a refusal any more: a host may carry more than one brand, so
			    this says who else is on it and leaves the decision here. */}
			{existing && (
				<div className="banner banner-note">
					<span>
						{t("onboard.website.claimedElsewhere", { name: existing.name })} {t("onboard.website.claimedHelp")}
					</span>
					{held && (
						<Button className="spacer" type="button" disabled={busy} onClick={() => setDraft(held)}>
							{t("onboard.website.addAnyway")}
						</Button>
					)}
				</div>
			)}
			<form onSubmit={analyze} className="stack">
				<div className="field">
					<label htmlFor="website">{t("onboard.website.label")}</label>
					<Input
						id="website"
						type="url"
						inputMode="url"
						placeholder="https://www.example.com"
						value={website}
						onChange={(e) => {
							setWebsite(e.target.value);
							setExisting(null);
							setHeld(null);
						}}
						required
					/>
				</div>
				<Button variant="primary" type="submit" disabled={busy || !website}>
					{busy ? t("onboard.website.analyzingShort") : t("onboard.continue")}
				</Button>
			</form>
			<div className="login-or">
				<span>{t("login.or")}</span>
			</div>
			<Button
				type="button"
				variant="default"
				className="login-provider"
				disabled={busy}
				// No website: the brand step starts empty and they describe it.
				onClick={advance}
			>
				{t("onboard.website.none")}
			</Button>
		</div>
	);
}

function Working({ label }: { label: string }) {
	return (
		<div className="wizard-working">
			<div className="wizard-orb" aria-hidden />
			<p>{label}</p>
		</div>
	);
}

// -- 3. the brand ------------------------------------------------------------------

function BrandStep({ state, entityId, orgId, setEntityId, refresh, advance }: StepProps) {
	const creationRequest = useRef<{ body: string; id: string } | null>(null);
	const t = useT();
	const { locale } = useI18n();
	// Only the brand this pass is building. An account adding its second brand
	// holds none yet, and the state's entity is then its first one.
	const e = state.entity?.id === entityId ? state.entity : null;
	const [name, setName] = useState(e?.name ?? "");
	const [description, setDescription] = useState(e?.description ?? "");
	// Only for a brand with no website: its facts have to come from somewhere
	// else that says what it makes, and a page it names is as citable as a site.
	const describedElsewhere = !e?.website;
	const [sourcePages, setSourcePages] = useState((e?.source_pages ?? []).join("\n"));
	const [categories, setCategories] = useState<string[]>(e?.categories ?? []);
	const categoryLabels = useCategoryLabels(t("onboard.brand.categories"));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<unknown>(null);

	async function submit(ev: FormEvent) {
		ev.preventDefault();
		setBusy(true);
		setError(null);
		try {
			const pages = describedElsewhere
				? sourcePages
						.split("\n")
						.map((line) => line.trim())
						.filter(Boolean)
				: undefined;
			const signature = JSON.stringify({ entityId, name, description, categories, pages });
			if (creationRequest.current?.body !== signature)
				creationRequest.current = { body: signature, id: crypto.randomUUID() };
			const written = await api.onboarding.brand({
				request_id: creationRequest.current.id,
				entity_id: entityId,
				org_id: orgId,
				name,
				description,
				categories,
				source_pages: pages,
			});
			setEntityId(written.entity_id);
			await refresh(written.entity_id);
			advance();
		} catch (err) {
			setError(err);
		} finally {
			setBusy(false);
		}
	}

	return (
		<form onSubmit={submit} className="stack">
			<div className="row" style={{ gap: "var(--s-6)" }}>
				{e?.logo_url && <img src={e.logo_url} alt="" className="size-(--s-14) rounded-control object-contain" />}
				<h1>{t(e ? "onboard.brand.confirmTitle" : "onboard.brand.title")}</h1>
			</div>
			<p className="muted">{t(e && !describedElsewhere ? "onboard.brand.confirmBody" : "onboard.brand.body")}</p>
			<Problem error={error} />
			<div className="field">
				<label htmlFor="brand-name">{t("onboard.brand.name")}</label>
				<Input
					id="brand-name"
					value={name}
					onChange={(ev) => setName(ev.target.value)}
					required
					minLength={2}
					maxLength={80}
				/>
			</div>
			<div className="field">
				<label htmlFor="brand-description">{t("onboard.brand.description")}</label>
				<Textarea
					id="brand-description"
					rows={4}
					maxLength={600}
					value={description}
					onChange={(ev) => setDescription(ev.target.value)}
					placeholder={t("onboard.brand.descriptionPlaceholder")}
				/>
				<span className="hint">{t("onboard.brand.descriptionHint")}</span>
			</div>
			{describedElsewhere && (
				<div className="field">
					<label htmlFor="brand-source-pages">{t("onboard.brand.sources")}</label>
					<Textarea
						id="brand-source-pages"
						rows={3}
						value={sourcePages}
						onChange={(ev) => setSourcePages(ev.target.value)}
						placeholder={t("onboard.brand.sourcesPlaceholder")}
					/>
					<span className="hint">{t("onboard.brand.sourcesHint")}</span>
				</div>
			)}
			<div className="field">
				<label htmlFor="brand-categories">{t("onboard.brand.categories")}</label>
				<CategorySelect
					id="brand-categories"
					value={categories}
					onChange={setCategories}
					locale={locale}
					labels={categoryLabels}
				/>
				<span className="hint">{t("onboard.brand.categoriesHint")}</span>
			</div>
			<Button variant="primary" type="submit" disabled={busy || name.trim().length < 2}>
				{busy ? t("login.working") : t("onboard.continue")}
			</Button>
		</form>
	);
}

// -- 4. region + language ----------------------------------------------------------

// Named by the browser in the reader's language, so the list is codes only.
const REGIONS = ["US", "CN", "GB", "DE", "JP", "SG", "IN", "BR", "AU", "CA"];

function RegionStep({ state, refresh, advance }: StepProps) {
	const t = useT();
	const { locale: ui, setLocale } = useI18n();
	const [region, setRegion] = useState(state.region ?? pick(ui, ["US", "CN"]));
	const [lang, setLang] = useState<"en" | "zh">(state.locale);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<unknown>(null);

	async function submit(ev: FormEvent) {
		ev.preventDefault();
		setBusy(true);
		setError(null);
		try {
			await api.onboarding.region({ region, locale: lang });
			setLocale(lang);
			await refresh();
			advance();
		} catch (err) {
			setError(err);
		} finally {
			setBusy(false);
		}
	}

	return (
		<form onSubmit={submit} className="stack">
			<h1>{t("onboard.region.title")}</h1>
			<p className="muted">{t("onboard.region.body")}</p>
			<Problem error={error} />
			<div className="field">
				<label htmlFor="region">{t("onboard.region.region")}</label>
				<NativeSelect id="region" value={region} onChange={(ev) => setRegion(ev.target.value)}>
					{REGIONS.map((code) => (
						<option key={code} value={code}>
							{fmt.region(ui, code)}
						</option>
					))}
				</NativeSelect>
			</div>
			<div className="field">
				<label htmlFor="lang">{t("onboard.region.language")}</label>
				<NativeSelect id="lang" value={lang} onChange={(ev) => setLang(ev.target.value as "en" | "zh")}>
					<option value="en">English (en)</option>
					<option value="zh">中文 (zh)</option>
				</NativeSelect>
				<span className="hint">{t("onboard.region.languageHint")}</span>
			</div>
			<Button variant="primary" type="submit" disabled={busy}>
				{busy ? t("login.working") : t("onboard.continue")}
			</Button>
		</form>
	);
}

// -- 6. topics ---------------------------------------------------------------------

const MAX_TOPICS = 5;
/** The dial's ends, and the server's: `deferred::PER_TOPIC_RANGE`. */
const PER_TOPIC_MIN = 1;
const PER_TOPIC_MAX = 10;

function clamp(n: number): number {
	return Number.isFinite(n) ? Math.min(PER_TOPIC_MAX, Math.max(PER_TOPIC_MIN, Math.round(n))) : 5;
}

function TopicsStep({ state, entityId, orgId, refresh, advance }: StepProps) {
	const t = useT();
	// Not retried: every attempt is a model call the account pays for.
	// A model call: asked once per visit to this step, never retried and never
	// refetched while the person is choosing from it.
	const suggestions = useQuery({
		queryKey: ["onboarding", "topics", entityId, orgId ?? null],
		queryFn: () => api.onboarding.topics(entityId ?? undefined, orgId),
		...paid,
		enabled: !state.entity?.selected_topics.length,
	});
	const [chosen, setChosen] = useState<string[]>(state.entity?.selected_topics ?? []);
	const [perTopic, setPerTopic] = useState(state.entity?.per_topic ?? 5);
	const [custom, setCustom] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<unknown>(null);
	const seeded = useRef(chosen.length > 0);

	// Pre-tick the first five, as Profound does; the person edits from there.
	useEffect(() => {
		if (seeded.current || !suggestions.data) return;
		seeded.current = true;
		setChosen(suggestions.data.topics.slice(0, MAX_TOPICS));
	}, [suggestions.data]);

	function toggle(topic: string) {
		setChosen((c) => (c.includes(topic) ? c.filter((x) => x !== topic) : c.length < MAX_TOPICS ? [...c, topic] : c));
	}

	async function write() {
		setBusy(true);
		setError(null);
		try {
			await api.onboarding.saveTopics(chosen, clamp(perTopic), entityId ?? undefined, orgId);
			await refresh();
			await advance();
		} catch (err) {
			setError(err);
		} finally {
			setBusy(false);
		}
	}

	const all = suggestions.data?.topics ?? [];
	const extras = chosen.filter((c) => !all.includes(c));

	return (
		<div className="stack">
			<h1>{t("onboard.topics.title")}</h1>
			<p className="muted">{t("onboard.topics.body", { max: String(MAX_TOPICS) })}</p>
			<Problem error={error ?? suggestions.error} />
			{suggestions.isFetching && <Working label={t("onboard.topics.suggesting")} />}
			<div className="wizard-chips">
				{[...all, ...extras].map((topic) => (
					<ChoiceChip key={topic} selected={chosen.includes(topic)} onClick={() => toggle(topic)}>
						{topic}
					</ChoiceChip>
				))}
			</div>
			<form
				className="row"
				onSubmit={(ev) => {
					ev.preventDefault();
					const v = custom.trim();
					if (v && !chosen.includes(v) && chosen.length < MAX_TOPICS) setChosen([...chosen, v]);
					setCustom("");
				}}
			>
				<Input
					className="grow"
					placeholder={t("onboard.topics.custom")}
					value={custom}
					onChange={(ev) => setCustom(ev.target.value)}
					maxLength={60}
				/>
				<Button variant="default" type="submit" disabled={!custom.trim() || chosen.length >= MAX_TOPICS}>
					+
				</Button>
			</form>
			<p className="hint">{t("onboard.topics.rule", { chosen: String(chosen.length), max: String(MAX_TOPICS) })}</p>
			<div className="field">
				<label htmlFor="per-topic">{t("onboard.topics.perTopic")}</label>
				<Input
					id="per-topic"
					// A two-digit field, not a full-width one: the width of a control
					// is a claim about how much you are being asked to type.
					className="w-20"
					type="number"
					min={PER_TOPIC_MIN}
					max={PER_TOPIC_MAX}
					value={perTopic}
					onChange={(ev) => setPerTopic(Number(ev.target.value))}
					// Clamped on the way out rather than while typing: clamping a
					// half-typed "1" of "10" back to 1 fights the person's hands.
					onBlur={() =>
						setPerTopic((n) => (Number.isFinite(n) ? Math.min(PER_TOPIC_MAX, Math.max(PER_TOPIC_MIN, n)) : 5))
					}
				/>
				<span className="hint">{t("onboard.topics.perTopicHint", { n: String(chosen.length * clamp(perTopic)) })}</span>
			</div>
			<Button variant="primary" type="button" disabled={busy || chosen.length === 0} onClick={write}>
				{busy ? t("login.working") : t("onboard.topics.toEngines")}
			</Button>
		</div>
	);
}

// -- 7. engines --------------------------------------------------------------------
//
// Which assistants are asked. Before the plan, because the count chosen here is
// what narrows the plans on the next screen: a person who wants four engines
// should not be sold a plan of one and find out from a chart with three
// columns missing.

function EnginesStep({ state, entityId, orgId, refresh, advance, last }: StepProps) {
	const t = useT();
	const { locale } = useI18n();
	const { current: org } = useWorkspaces();
	// The plan is changed here rather than at `/billing`: that address lives
	// under a brand the wizard has not finished creating, so following it left
	// the person on an empty workspace with the wizard's half-filled step gone.
	const [upgrading, setUpgrading] = useState(false);
	const e = state.entity?.id === entityId ? state.entity : null;
	const [chosen, setChosen] = useState<string[]>(e?.engines ?? []);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<unknown>(null);
	// An account that already pays has an answer to "how many": its plan's.
	// Offering the full board and cutting it on read is how somebody picks five
	// engines and finds three in the chart.
	const billing = useQuery({
		queryKey: [...keys.org(state.org_id), "billing"],
		queryFn: () => api.billing(state.org_id),
		enabled: state.paid,
		...read,
	});
	const current = billing.data?.current ?? null;
	const plan = current ? (billing.data?.plans.find((p) => p.key === current.key) ?? null) : null;
	const max = plan ? plan.engines_max : null;

	function toggle(key: string) {
		setChosen((c) => (c.includes(key) ? c.filter((x) => x !== key) : [...c, key]));
	}

	async function submit() {
		setBusy(true);
		setError(null);
		try {
			await api.onboarding.engines(chosen, entityId ?? undefined, orgId);
			await refresh();
			await advance();
		} catch (err) {
			setError(err);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="stack">
			<h1>{t("onboard.engines.title")}</h1>
			<p className="muted">{t("onboard.engines.body")}</p>
			<Problem error={error} />
			<EnginePicker chosen={chosen} max={max} onToggle={toggle} />
			{plan ? (
				<p className="hint">
					{t("onboard.engines.plan", {
						plan: String(pick(locale, [plan.name_en, plan.name_zh])),
						n: String(chosen.length),
						max: String(max ?? 0),
					})}{" "}
					<Button type="button" variant="link" onClick={() => setUpgrading(true)}>
						{t("plan.upgrade")}
					</Button>
				</p>
			) : (
				<>
					<p className="hint">{t("onboard.engines.limits")}</p>
					<p className="hint">{t("onboard.engines.chosen", { n: String(chosen.length) })}</p>
				</>
			)}
			<Button variant="primary" type="button" disabled={busy || chosen.length === 0} onClick={submit}>
				{busy ? t("login.working") : t(last ? "onboard.topics.toWorkspace" : "onboard.topics.toPlan")}
			</Button>
			{org ? (
				<Modal open={upgrading} onOpenChange={(next) => (next ? undefined : setUpgrading(false))}>
					<ModalContent size="xl">
						<ModalHeader>{t("plan.upgrade")}</ModalHeader>
						{/* The panel is a fragment of blocks, and the dialog is a flex
						    column: without a body around them the stats row is a flex
						    item that shrinks to nothing. */}
						<ModalBody>
							<SubscriptionPanel org={org} admin={org.role === "owner" || org.role === "admin"} />
						</ModalBody>
					</ModalContent>
				</Modal>
			) : null}
		</div>
	);
}

// -- 7. plan --------------------------------------------------------------------
//
// The gate, and the last screen. Free finishes with a listing and no
// measurement; paid goes to checkout and, on return, to the workspace. Nothing
// is measured here either way.

function PlanStep({ state, refresh }: StepProps) {
	const t = useT();
	const { locale } = useI18n();
	const plans = useQuery({
		queryKey: [...keys.org(state.org_id), "billing"],
		queryFn: () => api.billing(state.org_id),
		...read,
	});
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<unknown>(null);
	const leaving = useRef(false);

	/** The end of the wizard: whatever was decided here, the workspace opens. */
	const finish = useCallback(async () => {
		// The navigation is a whole-page load, so this guard is only about the
		// moment before it: the poll below and a click can both land on a paid
		// account and neither should finish the wizard twice.
		if (leaving.current) return;
		leaving.current = true;
		try {
			const done = await api.onboarding.finish();
			window.location.assign(`/w/${done.entity_id}`);
		} catch (err) {
			leaving.current = false;
			throw err;
		}
	}, []);

	// A plan can arrive without anybody clicking anything on this screen: an
	// operator grants one while it is open, or the customer paid, closed the
	// tab and came back. Either way the wizard carries on by itself — waiting
	// on a button for a decision that has already been made is how a paid
	// account ends up stuck at the price list.
	useEffect(() => {
		if (state.paid) {
			finish().catch(setError);
			return;
		}
		const timer = setInterval(() => {
			refresh().catch(() => {});
		}, 5000);
		return () => clearInterval(timer);
	}, [state.paid, refresh, finish]);

	async function startFree() {
		setBusy("listed");
		setError(null);
		try {
			await finish();
		} catch (err) {
			setError(err);
			setBusy(null);
		}
	}

	async function choose(planKey: string, interval: "month" | "year") {
		setBusy(planKey);
		setError(null);
		try {
			// Already subscribed (back from checkout, or granted a plan): done.
			const fresh = await refresh();
			if (fresh.paid) {
				await finish();
				return;
			}
			const checkout = await api.subscribe(state.org_id, {
				plan_key: planKey,
				interval,
				currency: "USD",
			});
			const completed = await openSubscriptionCheckout(checkout.checkout_url, t("billing.checkoutUnavailable"));
			if (completed) {
				if (await waitForSubscription(async () => (await refresh()).paid)) await finish();
				else toast.info(t("billing.confirming"));
			} else {
				await refresh();
			}
		} catch (err) {
			setError(err);
		} finally {
			setBusy(null);
		}
	}

	if (state.paid) {
		const current = plans.data?.current;
		const label = current ? String(pick(locale, [current.name_en, current.name_zh])) : "";
		return (
			<div className="stack">
				<h1>{t("onboard.plan.title")}</h1>
				<p className="muted">{t("onboard.plan.active", { plan: label })}</p>
				<Problem error={error} />
				<Button variant="primary" type="button" disabled={busy !== null} onClick={startFree}>
					{busy ? t("login.working") : t("onboard.plan.open")}
				</Button>
			</div>
		);
	}

	return (
		<div className="stack">
			<h1>{t("onboard.plan.title")}</h1>
			<p className="muted">{t("onboard.plan.body")}</p>
			<p className="hint">{t("onboard.plan.gate")}</p>
			<Problem error={error ?? plans.error} />
			<PlanChooser
				plans={plans.data?.plans ?? []}
				busy={busy}
				minEngines={state.entity?.engines.length ?? 0}
				onChoose={choose}
			/>
			<p className="login-foot">
				{t("onboard.plan.freeLine")}{" "}
				<Button type="button" variant="link" disabled={busy !== null} onClick={startFree}>
					{t("onboard.plan.startFree")}
				</Button>
			</p>
		</div>
	);
}
